import React, { useState, useEffect } from "react";
import { useUser } from "../context/UserContext";


function Teams({ teams, subTeams = [], departments = [], tasks = [], users = [], onCreateTeam, onDeleteTeam, onUpdateTeam, onCreateSubTeam, onDeleteSubTeam, onCreateTask, onRefreshUsers, onRefreshTeams, onCreateDepartment, onUpdateDepartment, onDeleteDepartment, darkMode }) {
  const { currentUser } = useUser();
  const hasTeamMgmtPermission = currentUser?.can_manage_teams === true;
  const isAdmin = currentUser?.role === "Global Admin" || currentUser?.role === "Operational Admin" || hasTeamMgmtPermission;
  const isDeptAdmin = currentUser?.role === "Department Admin" || (currentUser?.adminDeptIds && currentUser.adminDeptIds.length > 0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editingDeptName, setEditingDeptName] = useState("");


  const [form, setForm] = useState({
    name: "",
    department_id: "",
    newSubTeamName: ""
  });

  const [teamSearch, setTeamSearch] = useState("");

  const [memberModalTeam, setMemberModalTeam] = useState(null);
  const [selectedSubTeam, setSelectedSubTeam] = useState(null);
  useEffect(() => {
    if (onRefreshUsers) onRefreshUsers();
    if (onRefreshTeams) onRefreshTeams();
  }, [onRefreshUsers, onRefreshTeams]);

  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);

  const openCreateModal = () => {
    setEditingTeam(null);
    setForm({ name: "", department_id: "", newSubTeamName: "" });
    setIsModalOpen(true);
  };

  const openEditModal = (team) => {
    setEditingTeam(team);
    setForm({
      name: team.name,
      department_id: team.department_id || "",
      newSubTeamName: ""
    });
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (editingTeam) {
      onUpdateTeam(editingTeam.id, {
        name: form.name,
        department_id: form.department_id || null,
      });
    } else {
      onCreateTeam({
        name: form.name,
        department_id: form.department_id || null,
      });
    }

    setIsModalOpen(false);
  };

  const handleDelete = (teamId, teamName, e) => {
    if (e) e.stopPropagation();
    const ok = window.confirm(
      `Are you sure you want to delete team "${teamName}"?`
    );
    if (!ok) return;
    onDeleteTeam(teamId);
  };


  const handleCreateSubTeamClick = async () => {
    if (!editingTeam || !form.newSubTeamName.trim()) return;
    const name = form.newSubTeamName.trim();
    setForm(prev => ({ ...prev, newSubTeamName: "" }));
    await onCreateSubTeam({ name, team_id: editingTeam.id });
  };

  const handleDeleteSubTeamClick = async (id) => {
    if (window.confirm("Delete this sub-team?")) {
      await onDeleteSubTeam(id);
    }
  };


  const allowedTeamIds = [
    currentUser?.team_id,
    ...(currentUser?.memberships || []).map((m) => m.team_id)
  ].filter(Boolean);

  const adminDeptIds = [
    currentUser?.role === "Department Admin" ? currentUser?.department_id : null,
    ...(currentUser?.memberships || [])
      .filter((m) => m.is_department_admin || m.role === "Department Admin")
      .map((m) => m.department_id)
  ].filter(Boolean);

  const filteredTeams = teams.filter((team) => {
    if (isDeptAdmin) {
      if (adminDeptIds.includes(team.department_id)) return true;
      if (allowedTeamIds.includes(team.id)) return true;
      return false;
    } else if (!isAdmin) {
      return allowedTeamIds.includes(team.id);
    }

    const q = teamSearch.trim().toLowerCase();
    if (!q) return true;
    const inName = team.name.toLowerCase().includes(q);
    const dept = departments.find(d => d.id === team.department_id);
    const inDept = dept ? dept.name.toLowerCase().includes(q) : false;

    const teamMembers = users.filter(u => u.team_id === team.id);
    const inMembers = teamMembers.some((m) =>
      m.name.toLowerCase().includes(q)
    );
    return inName || inDept || inMembers;
  });

  const getCurrentMembers = () => {
    if (selectedSubTeam && selectedSubTeam.id !== 'ALL') {
      return users.filter(u => (u.sub_team_ids || []).includes(selectedSubTeam.id));
    }
    return users.filter(u => u.team_id === memberModalTeam?.id);
  };

  let selectedMemberTasks = [];
  if (memberModalTeam && selectedMember) {
    const memberName = typeof selectedMember === 'string' ? selectedMember : selectedMember.name;
    selectedMemberTasks = tasks.filter(
      (t) =>
        (t.assignee_id === selectedMember.id || t.assignee === memberName)
    );
  }

  return (
    <div className={`page ${darkMode ? 'dark bg-gray-900' : ''}`}>
      <div className="page-header" style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 32,
        background: darkMode ? "#4d7a22" : "#76AB3F",
        padding: "32px",
        borderRadius: "24px",
        color: "white",
        boxShadow: darkMode ? "0 10px 25px -5px rgba(0, 0, 0, 0.4)" : "0 10px 25px -5px rgba(16, 185, 129, 0.25)",
        display: "flex",
        transition: "all 0.3s ease"
      }}>
        <h2 style={{ color: "white", margin: 0, fontWeight: 800 }}>Teams</h2>
        <div className="page-header-right" style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <input id="searchTeams" name="searchTeams"
            className="team-search-input"
            placeholder="Search teams…"
            value={teamSearch}
            onChange={(e) => setTeamSearch(e.target.value)}
            style={{
              width: 240,
              background: darkMode ? "rgba(255,255,255,0.1)" : "white",
              color: darkMode ? "white" : "inherit",
              border: darkMode ? "none" : "1px solid var(--gray-200)"
            }}
          />

          {isAdmin && (
            <>
              <button className="btn-secondary" onClick={() => setIsDeptModalOpen(true)} style={{ background: "white", color: "#76AB3F", border: "none" }}>Manage Departments</button>
              <button className="btn-secondary" onClick={openCreateModal} style={{ background: "white", color: "#76AB3F", border: "none" }}>+ New Team</button>
            </>
          )}
        </div>
      </div>
      <div className="team-list">
        {filteredTeams.length === 0 && (
          <div className="card" style={{ padding: 16, fontStyle: "italic", color: darkMode ? "#9ca3af" : "var(--gray-500)", background: darkMode ? "#1f2937" : "white", border: darkMode ? "1px solid #374151" : "none" }}>
            No teams match your search.
          </div>
        )}

        {filteredTeams.map((team) => {

          if (isAdmin || isDeptAdmin) {
            return (
              <div
                className="team-list-item"
                key={team.id}
                onClick={() => {
                  setMemberModalTeam(team);
                  setSelectedSubTeam(null);
                  setMemberSearch("");
                  setSelectedMember(null);
                }}
                style={{
                  background: darkMode ? "#1f2937" : "white",
                  border: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)",
                  borderRadius: "var(--card-radius)",
                  padding: "16px",
                  marginBottom: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = "#76AB3F"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = darkMode ? "#374151" : "var(--gray-200)"}
              >
                <div className="team-list-info">
                  <div style={{ fontWeight: 600, fontSize: 16, color: darkMode ? "#f9fafb" : "var(--gray-900)" }}>{team.name}</div>
                  <div style={{ fontSize: 13, color: darkMode ? "#9ca3af" : "var(--gray-500)" }}>
                    {departments.find(d => d.id === team.department_id)?.name || "No Department"}
                  </div>
                  <div style={{ fontSize: 12, color: darkMode ? "#6b7280" : "var(--gray-400)", marginTop: 4 }}>
                    {users.filter(u => u.team_id === team.id).length} members
                  </div>
                </div>
                <div className="team-list-actions" style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(team);
                    }}
                  >
                    Edit
                  </button>
                  {isAdmin && (
                    <button
                      className="btn-danger"
                      onClick={(e) =>
                        handleDelete(team.id, team.name, e)
                      }
                      style={{ padding: "8px 16px" }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          }

          return (
            <div
              className="team-list-item"
              key={team.id}
              onClick={() => {
                setMemberModalTeam(team);
                setSelectedSubTeam(null);
                setMemberSearch("");
                setSelectedMember(null);
              }}
              style={{
                background: darkMode ? "#1f2937" : "white",
                border: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)",
                borderRadius: "var(--card-radius)",
                padding: "16px",
                marginBottom: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = "#76AB3F"}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = darkMode ? "#374151" : "var(--gray-200)"}
            >
              <div className="team-list-info">
                <div style={{ fontWeight: 600, fontSize: 16, color: darkMode ? "#f9fafb" : "var(--gray-900)" }}>{team.name}</div>
                <div style={{ fontSize: 13, color: darkMode ? "#9ca3af" : "var(--gray-500)" }}>
                  {departments.find(d => d.id === team.department_id)?.name || "No Department"}
                </div>
                <div style={{ fontSize: 12, color: darkMode ? "#4ade80" : "var(--primary-600)", marginTop: 4 }}>
                  {(() => {
                    const mNames = users.filter(u => u.team_id === team.id).map(u => u.name);
                    return mNames.length > 0 ? mNames.join(", ") : "No members";
                  })()}
                </div>
              </div>

              <div className="team-list-actions">
                <div style={{
                  background: darkMode ? "#1e3a24" : "var(--primary-50)",
                  color: darkMode ? "#4ade80" : "var(--primary-700)",
                  padding: "4px 10px",
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600
                }}>
                  {tasks.filter(t => t.team_id === team.id).length} tasks
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsModalOpen(false)} style={{ background: "rgba(17, 24, 39, 0.7)", backdropFilter: "blur(8px)" }}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{
            maxWidth: "680px",
            padding: 0,
            overflow: "hidden",
            borderRadius: "24px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            background: darkMode ? "#1f2937" : "white",
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column"
          }}>

            <div className="modal-header" style={{
              background: "#76AB3F",
              margin: 0,
              padding: "24px 32px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div>
                <h3 style={{ color: "white", margin: 0, fontSize: "20px", fontWeight: 700 }}>
                  {editingTeam ? "Edit Team Configuration" : "Create New Team"}
                </h3>
                <p style={{ margin: "4px 0 0", fontSize: "13px", color: "rgba(255, 255, 255, 0.8)" }}>
                  {editingTeam ? "Update team settings and manage sub-team assignments" : "Set up a new team and define its operational scope"}
                </p>
              </div>
              <button className="modal-close" onClick={() => setIsModalOpen(false)} style={{
                color: "white",
                background: "rgba(255, 255, 255, 0.15)",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
                border: "none"
              }}>✕</button>
            </div>

            <div style={{ padding: "20px 32px 32px", background: darkMode ? "#111827" : "white", overflowY: "auto", flex: 1 }}>
              <form onSubmit={handleSave}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  marginBottom: "20px"
                }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600 }}>Team Name <span className="required">*</span></label>
                    <input
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="e.g. IT Support"
                      style={{ padding: "10px 14px", fontSize: "15px", background: darkMode ? "#374151" : "white", color: darkMode ? "white" : "inherit", border: darkMode ? "1px solid #4b5563" : "1px solid var(--gray-200)" }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0, gridColumn: "span 2" }}>
                    <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600 }}>Department <span className="required">*</span></label>
                    <select
                      name="department_id"
                      required
                      value={form.department_id}
                      onChange={handleChange}
                      className="modern-select"
                      style={{ padding: "10px 14px", fontSize: "15px", width: "100%", background: darkMode ? "#374151" : "white", color: darkMode ? "white" : "inherit", border: darkMode ? "1px solid #4b5563" : "1px solid var(--gray-200)" }}
                    >
                      <option value="">Select Department</option>
                      {departments.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {editingTeam && (isAdmin) && (
                  <div style={{
                    marginTop: "24px",
                    paddingTop: "20px",
                    borderTop: darkMode ? "1px solid #374151" : "1px solid var(--gray-100)"
                  }}>
                    <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600, display: "block", marginBottom: "12px" }}>Sub-teams Management</label>

                    <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
                      <input
                        name="newSubTeamName"
                        value={form.newSubTeamName}
                        onChange={handleChange}
                        placeholder="Define new sub-team..."
                        style={{ flex: 1, padding: "10px 14px", borderRadius: "12px", background: darkMode ? "#111827" : "white", color: darkMode ? "white" : "inherit", border: darkMode ? "1px solid #4b5563" : "1px solid var(--gray-200)" }}
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleCreateSubTeamClick}
                        disabled={!form.newSubTeamName.trim()}
                        style={{ padding: "0 24px", borderRadius: "12px" }}
                      >Add</button>
                    </div>

                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: "12px",
                      maxHeight: "150px",
                      overflowY: "auto",
                      padding: "4px"
                    }}>
                      {subTeams.filter(st => st.team_id === editingTeam.id).map(st => (
                        <div key={st.id} style={{
                          background: darkMode ? "#1f2937" : "var(--gray-50)",
                          border: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)",
                          padding: "12px",
                          borderRadius: "16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          transition: "all 0.2s"
                        }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: "14px", color: darkMode ? "#f9fafb" : "var(--gray-800)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{st.name}</div>
                            <div style={{ fontSize: "11px", color: darkMode ? "#9ca3af" : "var(--gray-400)" }}>
                              {users.filter(u => (u.sub_team_ids || []).includes(st.id)).length} Members
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteSubTeamClick(st.id)}
                            style={{
                              width: "24px",
                              height: "24px",
                              borderRadius: "50%",
                              background: darkMode ? "#374151" : "white",
                              color: "#ef4444",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: darkMode ? "1px solid #4b5563" : "1px solid var(--gray-100)",
                              fontSize: "12px"
                            }}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "24px",
                  paddingTop: "20px",
                  borderTop: darkMode ? "1px solid #374151" : "1px solid var(--gray-100)"
                }}>
                  <button type="button" onClick={() => setIsModalOpen(false)} style={{
                    padding: "10px 24px",
                    borderRadius: "12px",
                    background: darkMode ? "#374151" : "var(--gray-50)",
                    color: darkMode ? "#d1d5db" : "var(--gray-700)",
                    fontWeight: 600,
                    border: "none"
                  }}>Cancel</button>
                  <button type="submit" style={{
                    padding: "10px 32px",
                    borderRadius: "12px",
                    background: "#76AB3F",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 14px rgba(118, 171, 63, 0.3)"
                  }}>
                    {editingTeam ? "Update Team" : "Create Team"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {memberModalTeam && (
        <div className="modal-backdrop" style={{ background: "rgba(17, 24, 39, 0.7)", backdropFilter: "blur(8px)" }}>
          <div className="modal-card" style={{
            maxWidth: "850px",
            padding: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
            borderRadius: "24px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            background: darkMode ? "#1f2937" : "white"
          }}>
            <div className="modal-header" style={{
              background: "#76AB3F",
              margin: 0,
              padding: "24px 32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
            }}>
              <div>
                <h3 style={{ color: "white", fontSize: "20px", fontWeight: 700, margin: 0 }}>{memberModalTeam.name} — Member Directory</h3>
                <p style={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.8)", margin: "4px 0 0" }}>Explore team composition and active work progress.</p>
              </div>
              <button
                className="modal-close"
                onClick={() => {
                  setMemberModalTeam(null);
                  setSelectedMember(null);
                  setSelectedSubTeam(null);
                }}
                style={{
                  color: "white",
                  background: "rgba(255, 255, 255, 0.15)",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none"
                }}
              >✕</button>
            </div>

            <div style={{ padding: "24px", overflowY: "auto", background: darkMode ? "#111827" : "white" }}>


              {(() => {
                const relevantSubTeams = subTeams.filter(st => st.team_id === memberModalTeam.id);
                const showNavigation = relevantSubTeams.length > 0 && !selectedSubTeam;

                if (showNavigation) {
                  return (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "20px" }}>
                      <div
                        onClick={() => setSelectedSubTeam({ id: 'ALL', name: 'Global Members' })}
                        style={{
                          background: darkMode ? "#1f2937" : "var(--gray-50)",
                          border: darkMode ? "2px solid #374151" : "2px solid var(--gray-100)",
                          borderRadius: "20px",
                          padding: "24px",
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = "#76AB3F"; e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 20px -8px rgba(118, 171, 63, 0.2)"; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? "#374151" : "var(--gray-100)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                      >
                        <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: "var(--primary-100)", color: "var(--primary-700)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          </svg>
                        </div>
                        <div style={{ fontSize: "17px", fontWeight: 700, color: darkMode ? "#f9fafb" : "var(--gray-900)", marginBottom: "4px" }}>Full Team</div>
                        <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "var(--gray-500)", fontWeight: 500 }}>
                          {users.filter(u => u.team_id === memberModalTeam.id).length} Total Members
                        </div>
                      </div>

                      {relevantSubTeams.map(st => (
                        <div
                          key={st.id}
                          onClick={() => setSelectedSubTeam(st)}
                          style={{
                            background: darkMode ? "#1f2937" : "white",
                            border: darkMode ? "2px solid #374151" : "2px solid var(--gray-100)",
                            borderRadius: "20px",
                            padding: "20px",
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = "#76AB3F"; e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 12px 20px -8px rgba(118, 171, 63, 0.2)"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? "#374151" : "var(--gray-100)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                        >
                          <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: "#F0FDF4", color: "var(--primary-600)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </div>
                          <div style={{ fontSize: "17px", fontWeight: 700, color: darkMode ? "#f9fafb" : "var(--gray-900)", marginBottom: "2px" }}>{st.name}</div>
                          <div style={{ fontSize: "11px", color: "#4ade80", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.05em", marginBottom: "8px" }}>
                            {departments.find(d => d.id === memberModalTeam.department_id)?.name || 'General'}
                          </div>
                          <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "var(--gray-500)", fontWeight: 500 }}>
                            {users.filter(u => (u.sub_team_ids || []).includes(st.id)).length} Members
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }

                // [MODERN] Back Button logic
                const hasBack = relevantSubTeams.length > 0 && selectedSubTeam;

                return (
                  <>
                    {hasBack && (
                      <button
                        onClick={() => { setSelectedSubTeam(null); setSelectedMember(null); }}
                        style={{
                          marginBottom: "24px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "10px 16px",
                          borderRadius: "12px",
                          background: darkMode ? "#1f2937" : "var(--gray-50)",
                          border: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)",
                          color: darkMode ? "#f9fafb" : "var(--gray-700)",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = darkMode ? "#374151" : "var(--gray-100)"}
                        onMouseLeave={e => e.currentTarget.style.background = darkMode ? "#1f2937" : "var(--gray-50)"}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Return to Team View
                      </button>
                    )}
                    {selectedSubTeam && (
                      <div style={{ marginBottom: "24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                          <h4 style={{ margin: 0, color: darkMode ? "#6b7280" : "var(--gray-400)", fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Current Selection</h4>
                          <div style={{ fontSize: "20px", fontWeight: 700, color: "#76AB3F", letterSpacing: "-0.01em" }}>{selectedSubTeam.name}</div>
                        </div>
                        <div style={{ background: darkMode ? "#1e3a24" : "var(--primary-50)", padding: "8px 16px", borderRadius: "12px", border: darkMode ? "1px solid #1e3a24" : "1px solid var(--primary-100)" }}>
                          <span style={{ color: darkMode ? "#4ade80" : "var(--primary-700)", fontWeight: 700 }}>{getCurrentMembers().length} Members</span>
                        </div>
                      </div>
                    )}

                    <div className="team-member-search-row" style={{ marginBottom: 16 }}>
                      <input id="searchMembers" name="searchMembers"
                        className="team-search-input"
                        placeholder="Search members…"
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        style={{
                          background: darkMode ? "#111827" : "white",
                          color: darkMode ? "white" : "inherit",
                          border: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)"
                        }}
                      />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: "24px", height: "450px" }}>
                      <div style={{ overflowY: "auto", paddingRight: "8px" }}>
                        {(getCurrentMembers())
                          .filter((m) =>
                            (m.name || m.email || "").toLowerCase().includes(memberSearch.trim().toLowerCase())
                          )
                          .map((m) => {
                            const userObj = m;
                            const photoUrl = userObj?.photo_url;

                            const isMe = userObj.id === currentUser?.id;
                            const canView = isAdmin || isDeptAdmin || isMe;

                            const initials = userObj.name
                              .split(" ")
                              .map((p) => p[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase();
                            const isSelected = selectedMember?.id === userObj.id;

                            return (
                              <div
                                key={userObj.id}
                                onClick={() => canView && setSelectedMember(userObj)}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                  padding: "12px 18px",
                                  borderRadius: "16px",
                                  cursor: canView ? "pointer" : "default",
                                  background: isSelected ? (darkMode ? "#064e3b" : "var(--primary-50)") : (darkMode ? "#1f2937" : "white"),
                                  border: `2px solid ${isSelected ? "#76AB3F" : "transparent"}`,
                                  marginBottom: "10px",
                                  transition: "all 0.2s ease",
                                  boxShadow: isSelected ? "0 4px 12px rgba(118, 171, 63, 0.15)" : "none",
                                  opacity: canView ? 1 : 0.6
                                }}
                                onMouseEnter={(e) => canView && !isSelected && (e.currentTarget.style.background = darkMode ? "#374151" : "var(--gray-50)")}
                                onMouseLeave={(e) => canView && !isSelected && (e.currentTarget.style.background = darkMode ? "#1f2937" : "white")}
                              >
                                <div style={{
                                  width: "42px",
                                  height: "42px",
                                  borderRadius: "12px",
                                  background: isSelected ? "#76AB3F" : (darkMode ? "#374151" : "var(--gray-100)"),
                                  color: isSelected ? "white" : (darkMode ? "#9ca3af" : "var(--gray-500)"),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  overflow: "hidden",
                                  transition: "all 0.2s"
                                }}>
                                  {photoUrl ? (
                                    <img src={photoUrl} alt={m} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  ) : (
                                    initials
                                  )}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, fontSize: "15px", color: isSelected ? (darkMode ? "white" : "var(--primary-700)") : (darkMode ? "#f3f4f6" : "var(--gray-800)"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userObj.name}</div>
                                  <div style={{ fontSize: "11px", color: isSelected ? (darkMode ? "#a7f3d0" : "var(--primary-600)") : (darkMode ? "#9ca3af" : "var(--gray-400)") }}>
                                    {isMe ? 'You' : (userObj?.role || 'Member')}
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                        {(getCurrentMembers()).length === 0 && (
                          <div style={{ textAlign: "center", padding: "40px", color: "var(--gray-400)" }}>
                            <p style={{ fontSize: "13px", margin: 0 }}>No matching members found.</p>
                          </div>
                        )}
                      </div>

                      <div style={{
                        background: darkMode ? "#1f2937" : "var(--gray-50)",
                        borderRadius: "24px",
                        padding: "24px",
                        overflowY: "auto",
                        border: darkMode ? "1px solid #374151" : "1px solid var(--gray-100)"
                      }}>
                        {selectedMember ? (
                          <>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "16px",
                              marginBottom: "24px",
                              paddingBottom: "20px",
                              borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)"
                            }}>
                              {(() => {
                                const u = (users || []).find(user => {
                                  const smName = (selectedMember?.name || selectedMember?.email || "").toLowerCase().trim();
                                  return user.name?.toLowerCase().trim() === smName ||
                                    user.email?.toLowerCase().trim() === smName;
                                });
                                const displayName = selectedMember?.name || selectedMember?.email || "Unknown";
                                const initials = displayName.split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();
                                return (
                                  <div style={{
                                    width: "64px",
                                    height: "64px",
                                    borderRadius: "18px",
                                    overflow: "hidden",
                                    background: darkMode ? "#111827" : "white",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "20px",
                                    fontWeight: 800,
                                    color: "var(--primary-600)"
                                  }}>
                                    {u?.photo_url ? (
                                      <img src={u.photo_url} alt={selectedMember} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    ) : initials}
                                  </div>
                                );
                              })()}
                              <div>
                                <h4 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#76AB3F", letterSpacing: "-0.01em" }}>{selectedMember?.name || selectedMember?.email || "Unknown Member"}</h4>
                                <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "2px" }}>
                                  <span style={{
                                    fontSize: "11px",
                                    background: darkMode ? "#1e3a24" : "var(--primary-100)",
                                    color: darkMode ? "#4ade80" : "var(--primary-700)",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    fontWeight: 700,
                                    textTransform: "uppercase"
                                  }}>
                                    {(users || []).find(u => {
                                      const smName = (selectedMember?.name || selectedMember?.email || "").toLowerCase().trim();
                                      return u.name?.toLowerCase().trim() === smName || u.email?.toLowerCase().trim() === smName;
                                    })?.role || 'User'}
                                  </span>
                                  <span style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "var(--gray-500)" }}>• Activity & Performance Overview</span>
                                </div>
                              </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

                              <div>
                                <h5 style={{ color: "#B45309", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 600 }}>
                                  <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", background: "#F59E0B" }}></span>
                                  In Progress
                                </h5>
                                {selectedMemberTasks.filter(t => t.status === "In Progress").length > 0 ? (
                                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                    {selectedMemberTasks.filter(t => t.status === "In Progress").map(t => (
                                      <div key={t.id} style={{
                                        background: darkMode ? "#111827" : "white",
                                        padding: "14px 18px",
                                        borderRadius: "16px",
                                        borderLeft: "4px solid #F59E0B",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center"
                                      }}>
                                        <div>
                                          <div style={{ fontWeight: 700, fontSize: "14px", color: darkMode ? "#f9fafb" : "var(--gray-900)" }}>{t.title}</div>
                                          <div style={{ fontSize: "11px", color: darkMode ? "#9ca3af" : "var(--gray-500)", marginTop: "4px" }}>Deadline: {t.due_date ? new Date(t.due_date).toLocaleDateString() : "Flexible"}</div>
                                        </div>
                                        <span style={{ fontSize: "10px", background: darkMode ? "#1e3a24" : "#FFFBEB", color: darkMode ? "#4ade80" : "#B45309", padding: "4px 10px", borderRadius: "20px", fontWeight: 800, textTransform: "uppercase" }}>Active</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div style={{ padding: "16px", borderRadius: "12px", border: "1px dashed var(--gray-200)", textAlign: "center", fontSize: "12px", color: "var(--gray-400)" }}>No active tasks at the moment.</div>
                                )}
                              </div>

                              {/* Todo List */}
                              <div>
                                <h5 style={{ color: "var(--gray-700)", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 600 }}>
                                  <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", background: "var(--gray-400)" }}></span>
                                  Todo
                                </h5>
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                  {selectedMemberTasks.filter(t => t.status === "Todo").map(t => (
                                    <div key={t.id} style={{
                                      padding: "10px 14px",
                                      fontSize: "13px",
                                      color: darkMode ? "#d1d5db" : "var(--gray-700)",
                                      background: darkMode ? "#111827" : "white",
                                      borderRadius: "12px",
                                      border: darkMode ? "1px solid #374151" : "1px solid var(--gray-100)",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px"
                                    }}>
                                      <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: darkMode ? "#4b5563" : "var(--gray-300)" }}></div>
                                      <span style={{ fontWeight: 500 }}>{t.title}</span>
                                    </div>
                                  ))}
                                  {selectedMemberTasks.filter(t => t.status === "Todo").length === 0 && (
                                    <div style={{ fontSize: "12px", color: "var(--gray-400)", fontStyle: "italic", paddingLeft: "14px" }}>No upcoming tasks.</div>
                                  )}
                                </div>
                              </div>

                              {/* Completed Work */}
                              <div>
                                <h5 style={{ color: "var(--primary-700)", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 600 }}>
                                  <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", background: "var(--primary-500)" }}></span>
                                  Completed
                                </h5>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "6px" }}>
                                  {selectedMemberTasks.filter(t => t.status === "Completed").map(t => (
                                    <div key={t.id} style={{
                                      padding: "8px 12px",
                                      borderRadius: "10px",
                                      border: darkMode ? "1px solid #064e3b" : "1px solid #F0FDF4",
                                      background: darkMode ? "#064e3b" : "#F0FDF4",
                                      color: darkMode ? "#4ade80" : "var(--primary-800)",
                                      fontSize: "12px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px"
                                    }}>
                                      <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "var(--primary-600)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9px" }}>✓</div>
                                      <span style={{ fontWeight: 600 }}>{t.title}</span>
                                    </div>
                                  ))}
                                  {selectedMemberTasks.filter(t => t.status === "Completed").length === 0 && (
                                    <div style={{ fontSize: "12px", color: "var(--gray-400)", fontStyle: "italic", paddingLeft: "14px" }}>No completed work yet.</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--gray-400)" }}>
                            <p style={{ fontSize: "13px", fontWeight: 500, margin: 0 }}>Select a team member to</p>
                            <p style={{ fontSize: "13px", margin: 0 }}></p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )
      }
      {
        isDeptModalOpen && (
          <div className="modal-backdrop" style={{ background: "rgba(17, 24, 39, 0.7)", backdropFilter: "blur(8px)" }}>
            <div className="modal-card" style={{ maxWidth: "500px", padding: 0, borderRadius: "24px", background: darkMode ? "#1f2937" : "white", overflow: "hidden" }}>
              <div className="modal-header" style={{ background: "#76AB3F", padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ color: "white", margin: 0 }}>Manage Departments</h3>
                <button onClick={() => setIsDeptModalOpen(false)} style={{ color: "white", background: "none", fontSize: "20px" }}>✕</button>
              </div>
              <div style={{ padding: "24px" }}>
                <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                  <input id="newdeptname" name="newdeptname"
                    value={newDeptName}
                    onChange={(e) => setNewDeptName(e.target.value)}
                    placeholder="New department name..."
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn-primary"
                    disabled={!newDeptName.trim()}
                    onClick={async () => {
                      await onCreateDepartment({ name: newDeptName.trim() });
                      setNewDeptName("");
                    }}
                  >Add</button>
                </div>

                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {departments.map(dept => (
                    <div key={dept.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-100)" }}>
                      {editingDeptId === dept.id ? (
                        <input id="editingdeptname" name="editingdeptname"
                          value={editingDeptName}
                          onChange={(e) => setEditingDeptName(e.target.value)}
                          onBlur={async () => {
                            if (editingDeptName.trim() && editingDeptName !== dept.name) {
                              await onUpdateDepartment(dept.id, { name: editingDeptName.trim() });
                            }
                            setEditingDeptId(null);
                          }}
                          autoFocus
                          style={{
                            background: darkMode ? "#111827" : "white",
                            color: darkMode ? "white" : "inherit",
                            border: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)",
                            padding: "4px 8px",
                            borderRadius: "4px"
                          }}
                        />
                      ) : (
                        <span onClick={() => { setEditingDeptId(dept.id); setEditingDeptName(dept.name); }} style={{ cursor: "pointer", color: darkMode ? "#f9fafb" : "inherit" }}>{dept.name}</span>
                      )}
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete department "${dept.name}"?`)) {
                            onDeleteDepartment(dept.id);
                          }
                        }}
                        style={{ color: "#ef4444", background: "none", border: "none" }}
                      >Delete</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}

export default Teams;
